import type { Point } from '../core/math';
import type { PathRequest, PathResponse } from './pathfinding-protocol';

const CELL_SIZE = 32;
const GRID_WIDTH = 128;
const GRID_HEIGHT = 50;

/** Tracks the newest path request per entity so late worker responses cannot revive old orders. */
export class PathRequestTracker {
  private readonly latestByEntity = new Map<number, number>();

  public begin(entityId: number, requestId: number): number | undefined {
    const previous = this.latestByEntity.get(entityId);
    this.latestByEntity.set(entityId, requestId);
    return previous;
  }

  public accept(response: PathResponse): boolean {
    if (this.latestByEntity.get(response.entityId) !== response.requestId) return false;
    this.latestByEntity.delete(response.entityId);
    return true;
  }

  public clear(): void {
    this.latestByEntity.clear();
  }
}

/** Presentation-side worker bridge. Simulation only receives validated waypoint paths. */
export class NavigationClient {
  private readonly worker = new Worker(new URL('./pathfinding.worker.ts', import.meta.url), {
    type: 'module',
  });
  private nextRequest = 1;
  private readonly callbacks = new Map<number, (points: Point[]) => void>();
  private readonly tracker = new PathRequestTracker();

  public constructor() {
    this.worker.onmessage = (event: MessageEvent<PathResponse>) => {
      const response = event.data;
      if (!this.tracker.accept(response)) {
        this.callbacks.delete(response.requestId);
        return;
      }
      const callback = this.callbacks.get(response.requestId);
      this.callbacks.delete(response.requestId);
      if (response.success && callback)
        callback(
          response.waypoints.map((point) => ({ x: point.x * CELL_SIZE, y: point.y * CELL_SIZE })),
        );
    };
  }
  public request(
    entityId: number,
    start: Point,
    goal: Point,
    movementClass: PathRequest['movementClass'],
    onPath: (points: Point[]) => void,
  ): void {
    const requestId = this.nextRequest++;
    const previous = this.tracker.begin(entityId, requestId);
    if (previous !== undefined) this.callbacks.delete(previous);
    this.callbacks.set(requestId, onPath);
    const request: PathRequest = {
      requestId,
      entityId,
      start: { x: start.x / CELL_SIZE, y: start.y / CELL_SIZE },
      goal: { x: goal.x / CELL_SIZE, y: goal.y / CELL_SIZE },
      movementClass,
      navigationRevision: 1,
      gridWidth: GRID_WIDTH,
      gridHeight: GRID_HEIGHT,
      blocked: [],
    };
    this.worker.postMessage(request);
  }
  public get pendingJobs(): number {
    return this.callbacks.size;
  }
  public dispose(): void {
    this.worker.terminate();
    this.callbacks.clear();
    this.tracker.clear();
  }
}
